import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TextField, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CircularProgress from '@material-ui/core/CircularProgress';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import get from 'lodash/get';

import Button from '../../core/components/Button';
import DialogView from '../../core/components/DialogView';
import { FeatureFlags } from '../../../../src/services/functions/FeatureFlag';
import { getDialogLoading, getCurrentOrder, getLoading } from '../../../selectors';
import AutoCompleteAddress from './AutoCompleteAddress';

import '../../../css/checkout/subComponents/DeliveryAddressDialog.scss';

const dialogContentFields = [
  {
    key: 'nickname',
    title: 'DeliveryAddressDialog.dialogContent.nickname',
    fieldWidth: '100%',
    isRequired: true,
  },
  {
    key: 'streetAddress',
    title: 'DeliveryAddressDialog.dialogContent.streetAddress',
    fieldWidth: '100%',
    isRequired: true,
  },
  {
    key: 'city',
    title: 'DeliveryAddressDialog.dialogContent.city',
    fieldWidth: '100%',
    isRequired: true,
  },
  {
    key: 'unit',
    title: 'DeliveryAddressDialog.dialogContent.unit',
    fieldWidth: '100%',
    details: [
      'DeliveryAddressDialog.dialogContent.addressDetails.apt',
      'DeliveryAddressDialog.dialogContent.addressDetails.suite',
      'DeliveryAddressDialog.dialogContent.addressDetails.floor',
      'DeliveryAddressDialog.dialogContent.addressDetails.room',
    ],
    isRequired: false,
  },
  {
    key: 'postalCode',
    title: 'DeliveryAddressDialog.dialogContent.postalCode',
    fieldWidth: '100%',
    isRequired: true,
  },
  {
    key: 'buzzNumber',
    title: 'DeliveryAddressDialog.dialogContent.buzzNumber',
    fieldWidth: '100%',
    isRequired: false,
  },
];

class DeliveryAddressDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState();
  }

  async componentDidMount() {
    this.getBrowserLocation();
  }

  getBrowserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.setState({
            browserCoordinates: {
              lat: position.coords.latitude,
              long: position.coords.longitude,
            },
          });
        },
        () => {
          console.log('Could not get browser geolocation, using default coordinates');
        },
      );
    }
  }

  getInitialState = () => (
    {
      nickname: '',
      streetAddress: '',
      unit: '',
      city: '',
      postalCode: '',
      buzzNumber: '',
      browserCoordinates: null,
      nicknameLeftBlank: false,
      streetAddressLeftBlank: false,
      cityLeftBlank: false,
      postalCodeLeftBlank: false,
    }
  );

  getDialogContent = () => {
    const { translation, loading } = this.props;
    if (loading !== 0) return <CircularProgress />;
    return (
      <div className="deliveryAddressDialog-dialogContentContainer">
        <div className="deliveryAddressDialog-dialogContentTextContainer">
          <Typography className="deliveryAddressDialog-dialogContentText">
            {translation('DeliveryAddressDialog.dialogContent.text')}
          </Typography>
        </div>
        {
          dialogContentFields.map((field) => {
            return (
              this.renderTextField(field, false, true)
            );
          })
        }
      </div>
    );
  }

  navigateBack = () => this.props.navigateBack()

  getActionButton = () => {
    const { translation, dialogLoading } = this.props;
    const isDisabled = this.disableButton();

    return (
      <div className="deliveryAddressDialog-actionButtonContainer">
        <Button
          variant="text"
          overrideClass
          type="primary"
          className="deliveryAddressDialog-backButtonStyle"
          onClick={this.navigateBack}
          startIcon={<ArrowBackIcon className="deliveryAddressDialog-backArrowIcon" />}
          text={translation('BACK')}
        />
        <Button
          type="primary"
          onClick={() => this.handleClickAdd()}
          disabled={isDisabled}
          overrideClass
          className="deliveryAddressDialog-addButtonOverride"
        >
          {
            dialogLoading !== 0
            ? <CircularProgress />
            : `${translation('DeliveryAddressDialog.addButton')}`
          }
        </Button>
      </div>
    );
  }

  disableButton = () => {
    const {
      nickname,
      streetAddress,
      city,
      postalCode,
    } = this.state;
    const { dialogLoading } = this.props;
    // unit can be null if it's a house and buzz number is optional
    return (dialogLoading !== 0) || !nickname || !streetAddress || !city || !postalCode;
  };

  handleChange = (fieldKey, event) => {
    const { target } = event;
    this.setState({
      [fieldKey]: target.value,
    });
  };

  handleClickAdd = async () => {
    const {
      actions,
      user,
      currentOrderId,
      handleClose,
      updateSelectedAddressId,
      closeAfterChange,
      currentOrder,
    } = this.props;
    const {
      nickname,
      streetAddress,
      unit,
      city,
      postalCode,
      buzzNumber,
    } = this.state;
    const addressObj = {
      nickname,
      street_address: streetAddress,
      unit_number: unit,
      city,
      postal_code: postalCode,
      buzzer_number: buzzNumber,
    };
    try {
      let addAddressResponse;
      if (user) {
        addAddressResponse = await actions.addAddress(user.token, addressObj, 'users', user.id, 'addresses');
        if (!addAddressResponse || addAddressResponse.error) {
          return;
        }
        await actions.getAllResources(
          user.token,
          ['users', user.id, 'addresses'],
        );
      } else {
        addAddressResponse = addressObj;
      }
      const updatedOrder = {
        ...currentOrder,
        address: addAddressResponse,
      };
      let updateOrderResponse;
      if (!user) {
        const addressChecked = await actions.checkAddress(user, addAddressResponse, ['locations', 'checkAddress']);
        if (addressChecked.location) updatedOrder.location = addressChecked.location;
        if (addressChecked.error) throw addressChecked.error;
      }
      if (!currentOrderId) {
        updateOrderResponse = await actions.updateOrder(null, updatedOrder, currentOrderId);
      } else {
        updateOrderResponse = await actions.updateOrder(user, updatedOrder, currentOrderId);
      }
      if (user && currentOrderId && (!updateOrderResponse || updateOrderResponse.error)) return;
      updateSelectedAddressId(user ? addAddressResponse.id : null);
      if (closeAfterChange) handleClose();
    } catch (error) {
      console.log('API call error', error);
    }
  };

  setAddress = (place) => {
    const formattedAddress = get(place, 'formatted_address');
    const addressComponents = get(place, 'address_components');
    // Since the format is "street address, city, province/state, country",
    // we'll assume the second term is going to be the address' city.
    const streetAddress = formattedAddress.split(',')[0];
    const city = formattedAddress.split(',')[1];
    // The place result also contains the postal code
    const postalCodeAddressComponent = addressComponents.find(component => component.types.includes('postal_code'))
    const postalCode = get(postalCodeAddressComponent, 'short_name');
    this.setState({ streetAddress, city, postalCode });
  }

  handleBlur = (field, inputText) => {
    if (this.isEmptyTextField(inputText)) this.setState({ [`${field}LeftBlank`]: true });
  }

  // Check for empty field or field with only blank spaces
  isEmptyTextField = (text) => {
    const filteredText = text !== undefined ? text.replace(/\s/g, '') : '';
    return filteredText === '';
  }

  renderTextField = (prop, isSelect, useFieldTitle) => {
    const {
      translation,
      width,
    } = this.props;

    let fieldTitle = translation(prop.title);

    if (fieldTitle === translation('DeliveryAddressDialog.dialogContent.postalCode')
      && FeatureFlags.CheckoutDrawer.DeliveryAddressDialog.provinceOrStateDropdown.state) {
      fieldTitle = translation('DeliveryAddressDialog.dialogContent.zipCode');
    }

    if (prop.details) {
      const details = prop.details.map(detail => translation(detail));
      fieldTitle = Array.join(details, '/');
    }

    if (prop.optionalText) {
      fieldTitle = `${fieldTitle} (${translation(prop.optionalText)})`;
    }

    if (prop.key === 'streetAddress') {
      return (
        <div className="deliveryAddressDialog-textFieldStyle" style={{ width: prop.fieldWidth }}>
          <AutoCompleteAddress
            id={prop.key}
            key={prop.key}
            select={isSelect}
            addressValue={this.state.streetAddress}
            label={useFieldTitle ? fieldTitle : translation(prop.title)}
            placeholder={useFieldTitle ? fieldTitle : translation(prop.title)}
            onPlaceSelected={place => this.setAddress(place)}
            className="deliveryAddressDialog-textFieldStyle"
            InputProps={this.getTextFieldInnerInputProps()}
            variant="outlined"
            FormHelperTextProps={{ className: 'deliveryAddressDialog-textFieldHelperText' }}
            helperText={this.renderHelperText(prop.isRequired, width)}
            emptyAddress={this.state.streetAddressLeftBlank}
            onAddressBlur={this.handleBlur}
            checkText={this.isEmptyTextField}
          />
        </div>
      );
    }

    return (
      <TextField
        id={prop.key}
        key={prop.key}
        select={isSelect}
        value={this.state[`${prop.key}`]}
        label={useFieldTitle ? fieldTitle : translation(prop.title)}
        placeholder={useFieldTitle ? fieldTitle : translation(prop.title)}
        onChange={event => this.handleChange(prop.key, event)}
        className="deliveryAddressDialog-textFieldStyle"
        style={{ width: prop.fieldWidth }}
        variant="outlined"
        helperText={this.renderHelperText(prop.isRequired, width)}
        InputProps={this.getTextFieldInnerInputProps()}
        FormHelperTextProps={{ className: 'deliveryAddressDialog-textFieldHelperText' }}
        error={prop.isRequired && this.isEmptyTextField(this.state[`${prop.key}`]) && this.state[`${prop.key}LeftBlank`]}
        id="outlined-error-helper-text"
        onBlur={() => { this.handleBlur(prop.key, this.state[`${prop.key}`]); }}
      />
    );
  }

  getTextFieldInnerInputProps = () => {
    const innerInputClasses = {
      root: {
        backgroundColor: 'var(--input-background-color)',
      },
    };
    return {
      classes: innerInputClasses,
    };
  }

  renderHelperText = (isRequired, width) => {
    const { translation } = this.props;
    const { hideTextFieldHelperTextOnMobile } = FeatureFlags.CheckoutDrawer.DeliveryAddressDialog;
    if (isRequired) {
      if (hideTextFieldHelperTextOnMobile && isWidthDown('sm', width)) return '';
      return translation('DeliveryAddressDialog.dialogContent.required');
    }
    // Render white text so all fields appear with the same margins
    return ' ';
  }

  render() {
    const {
      open, handleClose, allowDialogClose,
    } = this.props;

    return (
      <DialogView
        open={open}
        titleAlignClose={false}
        handleClose={() => handleClose()}
        disableBackdropClick={!allowDialogClose}
        disableEscapeKeyDown={!allowDialogClose}
        dialogTitleStyle="deliveryAddressDialog-dialogTitleStyle"
        dialogTitleStyleVariant="title"
        dialogBodyContainerStyle="deliveryAddressDialog-dialogBodyContainerStyle"
        dialogContentStyle="deliveryAddressDialog-dialogContentStyle"
        titleHasCloseBtn={allowDialogClose}
        hasDialogContent
        hasDialogContent2={false}
        renderDialogContent={() => this.getDialogContent()}
        hasDialogActions
        actionBtnStyle={{
          margin: '2% auto 4% auto',
          padding: 0,
        }}
        renderActionBtn={() => this.getActionButton()}
        dialogCloseIconColor="var(--text)"
      />
    );
  }
}

DeliveryAddressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  translation: PropTypes.func.isRequired,
  currentOrder: PropTypes.objectOf(PropTypes.any).isRequired,
  navigateBack: PropTypes.func.isRequired,
  user: PropTypes.objectOf(PropTypes.any),
  currentOrderId: PropTypes.number,
  closeAfterChange: PropTypes.bool,
  dialogLoading: PropTypes.number,
  loading: PropTypes.number,
  allowDialogClose: PropTypes.bool,
};

DeliveryAddressDialog.defaultProps = {
  closeAfterChange: true,
  user: null,
  currentOrderId: null,
  dialogLoading: 0,
  loading: 0,
  allowDialogClose: true,
};

const mapStateToProps = state => ({
  loading: getLoading(state),
  dialogLoading: getDialogLoading(state),
  currentOrder: getCurrentOrder(state),
});

export default connect(mapStateToProps)(withWidth()(DeliveryAddressDialog));
